/**
 * ⚠️  INTERNAL USE ONLY — NOT FOR PUBLIC ACCESS
 * Route: /admin/invoices
 * Password-gated. noindex. Not linked from any public page.
 * RepairShopr invoice dashboard with ticket comments.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, RefreshCw, FileText,
  CheckCircle2, Clock, AlertCircle, ChevronDown, ChevronUp,
  ArrowUpDown, ArrowUp, ArrowDown, MapPin, MessageSquare, X,
  DollarSign, Hash,
} from '../../lib/icons';
import SEOHead from '../../components/SEOHead';
import { AdminLoginGate, isAdminAuthed } from '../../components/AdminLoginGate';

// ── Types ─────────────────────────────────────────────────────────────────────
interface RsCustomer {
  id:         number;
  firstname?: string;
  lastname?:  string;
  email?:     string;
  phone?:     string;
}

interface RsInvoice {
  id:               number;
  number:           string | number;
  status:           string;   // 'Open' | 'Paid' | 'Draft' | 'Void'
  total:            string;
  balance?:         string;
  paid_at?:         string | null;
  due_date?:        string | null;
  created_at:       string;
  updated_at:       string;
  customer_id?:     number;
  customer?:        RsCustomer;
  ticket_id?:       number | null;
  ticket_ids?:      number[];
  // Added by proxy
  _location:        'san-diego' | 'temecula';
  _location_label:  string;
}

interface RsComment {
  id:         number;
  body:       string;
  tech?:      string;
  created_at: string;
  hidden?:    boolean;
}

interface RsTicket {
  id:        number;
  number?:   number;
  subject?:  string;
  body?:     string;
  status?:   string;
  comments?: RsComment[];
}

type LocationFilter = 'all' | 'san-diego' | 'temecula';
type StatusFilter   = 'all' | 'Open' | 'Paid';
type SortKey = 'number' | 'customer' | 'status' | 'created_at' | 'paid_at' | 'total' | 'location';


// ── Helpers ───────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.DEV ? 'http://localhost:3001' : '';

async function loadInvoices(location: LocationFilter, status: StatusFilter, page: number): Promise<{ invoices: RsInvoice[]; errors?: string[] }> {
  const qs = new URLSearchParams({ location, status, page: String(page) });
  const r = await fetch(`${API_BASE}/api/repairshopr-invoices?${qs}`);
  if (!r.ok) { const d = await r.json(); throw new Error(d.error ?? `HTTP ${r.status}`); }
  return r.json();
}

async function loadTicket(location: 'san-diego' | 'temecula', ticketId: number): Promise<RsTicket> {
  const qs = new URLSearchParams({ location, ticket_id: String(ticketId) });
  const r = await fetch(`${API_BASE}/api/repairshopr-ticket?${qs}`);
  if (!r.ok) { const d = await r.json(); throw new Error(d.error ?? `HTTP ${r.status}`); }
  return r.json();
}

const fmtMoney = (s?: string | number | null) => {
  const n = parseFloat(String(s ?? '0'));
  return isNaN(n) ? '—' : `$${n.toFixed(2)}`;
};

const fmtDate = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
};

const fmtDateShort = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }
  catch { return s; }
};

const fmtDateTime = (s?: string | null) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
  catch { return s; }
};

const customerName = (inv: RsInvoice) => {
  if (inv.customer?.firstname || inv.customer?.lastname) {
    return [inv.customer.firstname, inv.customer.lastname].filter(Boolean).join(' ');
  }
  return `Customer #${inv.customer_id ?? '?'}`;
};

const ticketId = (inv: RsInvoice): number | null =>
  inv.ticket_id ?? inv.ticket_ids?.[0] ?? null;

const STATUS_STYLE: Record<string, string> = {
  Paid:  'bg-green-500/20 text-green-300 border-green-500/40',
  Open:  'bg-blue-500/20  text-blue-300  border-blue-500/40',
  Draft: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40',
  Void:  'bg-gray-500/20  text-gray-400   border-gray-500/40',
};

const LOC_BADGE: Record<string, string> = {
  'san-diego': 'bg-purple-500/15 text-purple-300 border-purple-500/30',
  'temecula':  'bg-cyan-500/15   text-cyan-300   border-cyan-500/30',
};

// ── Ticket Comments Panel ─────────────────────────────────────────────────────
function TicketPanel({ invoice }: { invoice: RsInvoice }) {
  const tid = ticketId(invoice);
  const [ticket, setTicket]   = useState<RsTicket | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    if (!tid) { setLoading(false); return; }
    setLoading(true);
    loadTicket(invoice._location, tid)
      .then(t => setTicket(t))
      .catch(e => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [tid, invoice._location]);

  if (!tid) {
    return (
      <div className="px-6 py-4 text-sm text-gray-500 italic">
        No ticket linked to this invoice.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="px-6 py-4 flex items-center gap-2 text-gray-500 text-sm">
        <RefreshCw className="h-3.5 w-3.5 animate-spin" /> Loading ticket…
      </div>
    );
  }

  if (err) {
    return (
      <div className="px-6 py-4 flex items-center gap-2 text-red-400 text-sm">
        <AlertCircle className="h-3.5 w-3.5" /> {err}
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="px-6 py-4 space-y-3 bg-black/20">
      {/* Ticket header */}
      <div className="flex items-start gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Ticket #{ticket.number ?? tid}
            </span>
            {ticket.status && (
              <span className="px-1.5 py-0.5 rounded text-xs border bg-gray-700/40 text-gray-400 border-gray-600/40">
                {ticket.status}
              </span>
            )}
          </div>
          {ticket.subject && (
            <div className="mt-0.5 text-sm text-[#E0E0E0] font-medium">{ticket.subject}</div>
          )}
        </div>
      </div>

      {/* Ticket body (customer notes from booking) */}
      {ticket.body && (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-3">
          <div className="text-xs text-gray-500 mb-1.5 uppercase tracking-wide">Customer Notes</div>
          <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{ticket.body}</div>
        </div>
      )}

      {/* Comments */}
      {ticket.comments && ticket.comments.length > 0 ? (
        <div className="space-y-2">
          <div className="text-xs text-gray-500 uppercase tracking-wide">
            Comments ({ticket.comments.filter(c => !c.hidden).length})
          </div>
          {ticket.comments.filter(c => !c.hidden).map(comment => (
            <div key={comment.id} className="bg-gray-800/40 border border-gray-700/40 rounded-xl p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-medium text-gray-400">
                  {comment.tech || 'Tech'}
                </span>
                <span className="text-xs text-gray-600">{fmtDateTime(comment.created_at)}</span>
              </div>
              <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">
                {comment.body}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-gray-600 italic">No comments on this ticket.</div>
      )}
    </div>
  );
}

// ── Invoice Row ───────────────────────────────────────────────────────────────
function InvoiceRow({ inv, showLocation, expanded, onToggle }: {
  inv: RsInvoice;
  showLocation: boolean;
  expanded: boolean;
  onToggle: () => void;
}) {
  const statusStyle = STATUS_STYLE[inv.status] ?? STATUS_STYLE.Void;
  const tid = ticketId(inv);

  return (
    <>
      <tr
        className={`border-b border-gray-800/60 hover:bg-gray-900/30 transition-colors cursor-pointer ${expanded ? 'bg-gray-900/20' : ''}`}
        onClick={onToggle}
      >
        {/* Invoice # */}
        <td className="px-4 py-3 text-sm font-mono text-[#E0E0E0]">
          {inv.number || inv.id}
        </td>

        {/* Customer */}
        <td className="px-4 py-3">
          <div className="text-sm text-[#E0E0E0]">{customerName(inv)}</div>
          {inv.customer?.email && (
            <div className="text-xs text-gray-500 mt-0.5">{inv.customer.email}</div>
          )}
        </td>

        {/* Location (only when showing all) */}
        {showLocation && (
          <td className="px-4 py-3">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${LOC_BADGE[inv._location] ?? ''}`}>
              <MapPin className="h-2.5 w-2.5" />
              {inv._location_label}
            </span>
          </td>
        )}

        {/* Status */}
        <td className="px-4 py-3">
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border font-medium ${statusStyle}`}>
            {inv.status === 'Paid' && <CheckCircle2 className="h-3 w-3" />}
            {inv.status === 'Open' && <Clock className="h-3 w-3" />}
            {inv.status}
          </span>
        </td>

        {/* Created */}
        <td className="px-4 py-3 text-sm text-gray-400">
          {fmtDateShort(inv.created_at)}
        </td>

        {/* Paid Date */}
        <td className="px-4 py-3 text-sm text-gray-400">
          {inv.status === 'Paid' ? fmtDate(inv.paid_at) : '—'}
        </td>

        {/* Amount */}
        <td className="px-4 py-3 text-sm font-medium text-[#E0E0E0] text-right">
          {fmtMoney(inv.total)}
        </td>

        {/* Ticket */}
        <td className="px-4 py-3">
          {tid ? (
            <span className="flex items-center gap-1 text-xs text-gray-400">
              <Hash className="h-3 w-3" />{tid}
            </span>
          ) : (
            <span className="text-gray-700 text-xs">—</span>
          )}
        </td>

        {/* Expand toggle */}
        <td className="px-4 py-3">
          <button
            onClick={e => { e.stopPropagation(); onToggle(); }}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
          >
            <MessageSquare className="h-3.5 w-3.5" />
            {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
          </button>
        </td>
      </tr>

      {/* Expanded ticket panel */}
      {expanded && (
        <tr className="border-b border-gray-800/60">
          <td colSpan={showLocation ? 9 : 8} className="p-0">
            <TicketPanel invoice={inv} />
          </td>
        </tr>
      )}
    </>
  );
}

// ── Sort Header ───────────────────────────────────────────────────────────────
function SortTh({
  label, sortKey, current, onSort, className = '',
}: {
  label: string;
  sortKey: SortKey;
  current: { key: SortKey; dir: 'asc' | 'desc' };
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = current.key === sortKey;
  return (
    <th
      className={`px-4 py-3 text-left text-xs text-gray-500 uppercase tracking-wide cursor-pointer select-none hover:text-gray-300 transition-colors ${className}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="flex items-center gap-1">
        {label}
        {active
          ? current.dir === 'asc'
            ? <ArrowUp className="h-3 w-3 text-[#FF1744]" />
            : <ArrowDown className="h-3 w-3 text-[#FF1744]" />
          : <ArrowUpDown className="h-3 w-3 opacity-30" />}
      </span>
    </th>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
function InvoicesDashboard() {
  const [invoices, setInvoices]       = useState<RsInvoice[]>([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [warnings, setWarnings]       = useState<string[]>([]);
  const [locationFilter, setLocationFilter] = useState<LocationFilter>('all');
  const [statusFilter, setStatusFilter]     = useState<StatusFilter>('all');
  const [sort, setSort]               = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'created_at', dir: 'desc' });
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setWarnings([]);
    setExpandedIds(new Set());
    try {
      const data = await loadInvoices(locationFilter, statusFilter, 1);
      setInvoices(data.invoices);
      if (data.errors?.length) setWarnings(data.errors);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [locationFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleExpand = useCallback((id: number) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); } else { next.add(id); }
      return next;
    });
  }, []);

  const handleSort = useCallback((key: SortKey) => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'created_at' || key === 'paid_at' ? 'desc' : 'asc' }
    );
  }, []);

  const sorted = useMemo(() => {
    const getSortVal = (inv: RsInvoice): string | number => {
      switch (sort.key) {
        case 'number':     return String(inv.number ?? inv.id);
        case 'customer':   return customerName(inv).toLowerCase();
        case 'status':     return inv.status;
        case 'created_at': return inv.created_at ?? '';
        case 'paid_at':    return inv.paid_at ?? '';
        case 'total':      return parseFloat(inv.total) || 0;
        case 'location':   return inv._location_label;
        default:           return '';
      }
    };
    return [...invoices].sort((a, b) => {
      const av = getSortVal(a);
      const bv = getSortVal(b);
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sort.dir === 'asc' ? cmp : -cmp;
    });
  }, [invoices, sort]);

  // Stats
  const openCount        = invoices.filter(i => i.status === 'Open'  || i.status === 'Draft').length;
  const paidCount        = invoices.filter(i => i.status === 'Paid').length;
  const outstanding      = invoices
    .filter(i => i.status !== 'Paid' && i.status !== 'Void')
    .reduce((sum, i) => sum + (parseFloat(i.balance ?? i.total) || 0), 0);
  const totalPaid        = invoices
    .filter(i => i.status === 'Paid')
    .reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);

  const showLocation = locationFilter === 'all';

  const STATUS_TABS: { id: StatusFilter; label: string }[] = [
    { id: 'all',  label: 'All'  },
    { id: 'Open', label: 'Open' },
    { id: 'Paid', label: 'Paid' },
  ];

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#E0E0E0]">
      <SEOHead title="Invoices — Admin" description="" noindex />

      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="text-gray-500 hover:text-gray-300 transition-colors">
              <Home className="h-4 w-4" />
            </Link>
            <span className="text-gray-700">/</span>
            <FileText className="h-5 w-5 text-[#FF1744]" />
            <h1 className="font-bold text-[#E0E0E0]">Invoices</h1>
            <span className="text-xs text-gray-600 bg-gray-800 px-2 py-0.5 rounded-full">RepairShopr</span>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 hover:bg-gray-700 text-gray-300 text-sm transition-colors disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Shown',        value: invoices.length,     icon: FileText,      color: 'text-gray-400', fmt: (v: number) => String(v) },
            { label: 'Open',         value: openCount,           icon: Clock,         color: 'text-blue-400',  fmt: (v: number) => String(v) },
            { label: 'Paid',         value: paidCount,           icon: CheckCircle2,  color: 'text-green-400', fmt: (v: number) => String(v) },
            { label: 'Outstanding',  value: outstanding,         icon: DollarSign,    color: 'text-yellow-400', fmt: (v: number) => fmtMoney(v) },
          ].map(({ label, value, icon: Icon, color, fmt }) => (
            <div key={label} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-4 w-4 ${color}`} />
                <span className="text-xs text-gray-500">{label}</span>
              </div>
              <div className="text-2xl font-bold text-[#E0E0E0]">{fmt(value)}</div>
              {label === 'Paid' && (
                <div className="text-xs text-gray-500 mt-0.5">{fmtMoney(totalPaid)} collected</div>
              )}
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          {/* Status tabs */}
          <div className="flex gap-1 bg-gray-900 border border-gray-800 rounded-xl p-1">
            {STATUS_TABS.map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setStatusFilter(id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  statusFilter === id ? 'bg-[#FF1744] text-white' : 'text-gray-400 hover:text-[#E0E0E0]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Location */}
          <select
            value={locationFilter}
            onChange={e => setLocationFilter(e.target.value as LocationFilter)}
            className="bg-gray-900 border border-gray-800 rounded-xl px-3 py-2 text-sm text-[#E0E0E0] focus:outline-none focus:border-gray-600"
          >
            <option value="all">Both Locations</option>
            <option value="san-diego">San Diego</option>
            <option value="temecula">Temecula</option>
          </select>

          <span className="text-xs text-gray-600 ml-auto">
            {sorted.length} invoice{sorted.length !== 1 ? 's' : ''} · up to 100 per location
          </span>
        </div>

        {/* Warnings from partial failures */}
        {warnings.map(w => (
          <div key={w} className="flex items-center gap-2 bg-yellow-900/20 border border-yellow-800/40 rounded-xl px-4 py-3 text-yellow-400 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {w}
            <button onClick={() => setWarnings(prev => prev.filter(x => x !== w))} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 bg-red-900/20 border border-red-800/40 rounded-xl px-4 py-3 text-red-400 text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" />
            {error}
            <button onClick={() => setError(null)} className="ml-auto">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Table */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-gray-500 text-sm gap-2">
              <RefreshCw className="h-4 w-4 animate-spin" />
              Fetching invoices from RepairShopr…
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-500 gap-2">
              <FileText className="h-8 w-8 text-gray-700" />
              <span className="text-sm">No invoices found</span>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800">
                    <SortTh label="Invoice #"   sortKey="number"     current={sort} onSort={handleSort} />
                    <SortTh label="Customer"    sortKey="customer"   current={sort} onSort={handleSort} />
                    {showLocation && <SortTh label="Location" sortKey="location" current={sort} onSort={handleSort} />}
                    <SortTh label="Status"      sortKey="status"     current={sort} onSort={handleSort} />
                    <SortTh label="Created"     sortKey="created_at" current={sort} onSort={handleSort} />
                    <SortTh label="Paid Date"   sortKey="paid_at"    current={sort} onSort={handleSort} />
                    <SortTh label="Amount"      sortKey="total"      current={sort} onSort={handleSort} className="text-right" />
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase tracking-wide">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs text-gray-500 uppercase tracking-wide">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(inv => (
                    <InvoiceRow
                      key={`${inv._location}-${inv.id}`}
                      inv={inv}
                      showLocation={showLocation}
                      expanded={expandedIds.has(inv.id)}
                      onToggle={() => toggleExpand(inv.id)}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {!loading && sorted.length > 0 && (
          <p className="text-xs text-gray-600 text-center">
            Showing most recent {sorted.length} invoices. Use the RepairShopr dashboard for full history.
          </p>
        )}
      </div>
    </div>
  );
}

// ── Page Root ─────────────────────────────────────────────────────────────────
export default function AdminInvoicesPage() {
  const [authed, setAuthed] = useState(isAdminAuthed());
  return authed
    ? <InvoicesDashboard />
    : <AdminLoginGate onAuth={() => setAuthed(true)} />;
}
